import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchAllFlows, fetchFlowDetails } from '../../services/flowApiService';

// Async thunk to fetch all flows from API
export const fetchFlowsAsync = createAsyncThunk(
  'flows/fetchAll',
  async () => {
    const flows = await fetchAllFlows();
    return flows;
  }
);

// Async thunk to fetch single flow with details
export const fetchFlowWithDetailsAsync = createAsyncThunk(
  'flows/fetchWithDetails',
  async (flowId, { getState }) => {
    const state = getState();
    const cachedFlows = state.getFlows.flows;

    let flowMetadata = null;

    // Check if we have the flow metadata in cache
    if (cachedFlows && cachedFlows.length > 0) {
      flowMetadata = cachedFlows.find(f => f.flow_db_id === parseInt(flowId));
    }

    // If not in cache, fetch all flows
    if (!flowMetadata) {
      const allFlows = await fetchAllFlows();
      flowMetadata = allFlows.find(f => f.flow_db_id === parseInt(flowId));

      if (!flowMetadata) {
        throw new Error(`Flow ${flowId} not found in database`);
      }
    }

    // Then get the detailed events
    const events = await fetchFlowDetails(flowId);

    // Combine metadata with events
    return {
      ...flowMetadata,
      events
    };
  }
);

const getFlowsSlice = createSlice({
  name: 'getFlows',
  initialState: {
    flows: [],
    flowsById: {}, // Store individual flows with their details
    loading: false,
    error: null,
  },
  reducers: {
    clearFlowsCache: (state) => {
      state.flows = [];
      state.flowsById = {};
      state.error = null;
    },
    updateFlowInCache: (state, action) => {
      const flow = action.payload;
      if (flow.flow_db_id) {
        state.flowsById[flow.flow_db_id] = flow;
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch all flows
    builder
      .addCase(fetchFlowsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlowsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.flows = action.payload;
        // Also update the flowsById cache
        action.payload.forEach(flow => {
          if (flow.flow_db_id) {
            // Only update if we don't have detailed data already
            if (!state.flowsById[flow.flow_db_id]?.events) {
              state.flowsById[flow.flow_db_id] = flow;
            }
          }
        });
      })
      .addCase(fetchFlowsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Fetch single flow with details
    builder
      .addCase(fetchFlowWithDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlowWithDetailsAsync.fulfilled, (state, action) => {
        state.loading = false;
        const flow = action.payload;
        if (flow.flow_db_id) {
          state.flowsById[flow.flow_db_id] = flow;
        }
      })
      .addCase(fetchFlowWithDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearFlowsCache, updateFlowInCache } = getFlowsSlice.actions;

// Selectors
export const selectAllFlows = (state) => state.getFlows.flows;
export const selectFlowById = (flowId) => (state) => {
  const id = parseInt(flowId);
  return state.getFlows.flowsById[id] || null;
};
export const selectFlowsLoading = (state) => state.getFlows.loading;
export const selectFlowsError = (state) => state.getFlows.error;

export default getFlowsSlice.reducer;